import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { Flip } from "gsap/Flip";

import { breakpoints } from "@tokens/breakpoints";

import { stopScroller, startScroller } from "../utils/functions";
import { recomputeCursorState, resetCursorState } from "../components/cursor";
import { revealText } from "../base/split-text";
import { prefersReducedMotion } from "../utils/variables";

const expandProp = "borderRadius";
const navBreakMq = window.matchMedia(`(max-width: ${breakpoints["nav-break"] - 1}px)`);

let menuEl: HTMLDialogElement | null = null;
let menuTrigger: HTMLButtonElement | null = null;
let menuTriggerScrollTrigger: ScrollTrigger | null = null;
let suppressTriggerShow = false;
let expandEl: HTMLDivElement | null = null;
let lastOpenOrigin: HTMLElement | null = null;
let isAnimating = false;
let pendingClose: Promise<void> | null = null;

function hasLayout(el: Element): boolean {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
}

const inertTargetsSelector = '.skip-link, [data-barba="container"], .footer, .menu__trigger';

function setBackgroundInert(isInert: boolean) {
    document.querySelectorAll<HTMLElement>(inertTargetsSelector).forEach(el => {
        el.inert = isInert;
    });
}

function restoreFocus() {
    const target = lastOpenOrigin;
    if (target && document.contains(target) && hasLayout(target)) {
        target.focus();
    } else {
        menuTrigger?.focus();
    }
}

function ensureExpandEl(menu: HTMLDialogElement): HTMLDivElement {
    if (expandEl) return expandEl;

    expandEl = document.createElement("div");
    expandEl.className = "menu__expand";
    expandEl.setAttribute("aria-hidden", "true");
    gsap.set(expandEl, { autoAlpha: 0 });
    menu.parentElement?.insertBefore(expandEl, menu);

    return expandEl;
}

function createMenuTriggerScrollTrigger() {
    if (!menuTrigger) return;

    menuTriggerScrollTrigger?.kill();
    gsap.set(menuTrigger, { autoAlpha: 0 });

    menuTriggerScrollTrigger = ScrollTrigger.create({
        start: "100svh top",
        onEnter: () => gsap.to(menuTrigger!, { autoAlpha: 1, duration: 0.3 }),
        onLeaveBack: () => gsap.to(menuTrigger!, { autoAlpha: 0, duration: 0.3 }),
    });
}

export function resetMenuTrigger() {
    suppressTriggerShow = false;
    createMenuTriggerScrollTrigger();
}

export function hideMenuTrigger() {
    if (expandEl) {
        gsap.killTweensOf(expandEl);
        gsap.set(expandEl, { autoAlpha: 0 });
    }

    if (!menuTrigger) return;

    suppressTriggerShow = true;
    gsap.killTweensOf(menuTrigger);
    gsap.to(menuTrigger, { autoAlpha: 0, duration: 0.2 });
}

function isTriggerActive(): boolean {
    const st = menuTriggerScrollTrigger;
    return !!st && st.scroll() >= st.start;
}

function setLogoVisible(visible: boolean) {
    const logoEl = document.querySelector<HTMLElement>(".logo");
    if (!logoEl || (!visible && !navBreakMq.matches)) return;

    gsap.killTweensOf(logoEl);
    gsap.to(logoEl, { autoAlpha: visible ? 1 : 0, duration: 0.2 });
}

function setTriggerExpanded(expanded: boolean) {
    document.querySelectorAll<HTMLElement>('button[aria-controls="menu"]').forEach(trigger => {
        trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
    });
}

interface CloseMenuOptions {
    keepTriggerHidden?: boolean;
}

export function closeMenu(options: CloseMenuOptions = {}): Promise<void> {
    const menu = menuEl;
    if (!menu || !menu.open) return Promise.resolve();
    if (pendingClose) return pendingClose;

    if (isAnimating) {
        gsap.killTweensOf(menu);
        if (expandEl) gsap.killTweensOf(expandEl);
        if (menuTrigger) gsap.killTweensOf(menuTrigger);
        gsap.set(".menu__inner", { clearProps: "backgroundColor" });

        setTriggerExpanded(false);
        suppressTriggerShow = true;
        setLogoVisible(true);

        menu.close();
        startScroller();
        setBackgroundInert(false);
        restoreFocus();

        isAnimating = false;

        return Promise.resolve();
    }

    isAnimating = true;

    const showTrigger = !options.keepTriggerHidden && isTriggerActive();

    pendingClose = new Promise<void>(resolve => {
        const finish = () => {
            setTriggerExpanded(false);
            suppressTriggerShow = true;
            setLogoVisible(true);

            menu.close();
            startScroller();
            setBackgroundInert(false);
            restoreFocus();

            isAnimating = false;
            pendingClose = null;

            resolve();
        };

        gsap.killTweensOf(menu);
        if (menuTrigger) gsap.killTweensOf(menuTrigger);

        const originEl = lastOpenOrigin && hasLayout(lastOpenOrigin) ? lastOpenOrigin : menuTrigger;
        const panel = menu.querySelector<HTMLElement>(".menu__inner");
        const panelContent = menu.querySelector<HTMLElement>(".menu__dsk");

        if (!navBreakMq.matches && !prefersReducedMotion && expandEl && originEl && hasLayout(originEl) && panel && hasLayout(panel) && panelContent) {
            const expand = expandEl;
            gsap.killTweensOf(expand);

            Flip.fit(expand, panel, { duration: 0, props: expandProp });

            const shrink = Flip.fit(expand, originEl, {
                duration: 0.35,
                ease: "power3.inOut",
                absolute: true,
                props: expandProp,
            }) as gsap.core.Tween;

            gsap.set(expand, { autoAlpha: 1 });
            gsap.set(panel, { backgroundColor: "transparent" });

            gsap.timeline({ onComplete: finish })
                .add(shrink)
                .to(panelContent, { autoAlpha: 0, duration: 0.2, ease: "power1.out" }, 0)
                .add(hideActiveChip(panelContent), 0)
                .set(menu, { autoAlpha: 0 }, 0.2)
                .set(panel, { clearProps: "backgroundColor" }, 0.2)
                .add(() => {
                    if (menuTrigger && showTrigger) gsap.set(menuTrigger, { autoAlpha: 1 });
                })
                .to(expand, { autoAlpha: 0, duration: 0.1 });
        } else {
            const tl = gsap.timeline({ onComplete: finish }).to(menu, { autoAlpha: 0, duration: 0.2 });
            if (menuTrigger && showTrigger) tl.to(menuTrigger, { autoAlpha: 1, duration: 0.2 }, "<");
        }
    });

    return pendingClose;
}

function openMenu(origin: HTMLElement) {
    const menu = menuEl;
    if (!menu || menu.open || isAnimating) return;

    isAnimating = true;
    lastOpenOrigin = origin;

    setLogoVisible(false);
    stopScroller();

    gsap.killTweensOf(menu);
    gsap.set(menu, { autoAlpha: 0 });

    if (menuTrigger) gsap.to(menuTrigger, { autoAlpha: 0, duration: 0.2 });

    menu.show();
    setBackgroundInert(true);
    resetCursorState();
    recomputeCursorState();

    setTriggerExpanded(true);

    const closeBtn = menu.querySelector<HTMLElement>(navBreakMq.matches ? ".menu__mbl__close" : ".menu__dsk__nav__close");
    (closeBtn ?? menu).focus();

    const panel = menu.querySelector<HTMLElement>(".menu__inner");
    const panelContent = menu.querySelector<HTMLElement>(".menu__dsk");

    if (!navBreakMq.matches && !prefersReducedMotion && hasLayout(origin) && panel && hasLayout(panel)) {
        const expand = ensureExpandEl(menu);
        const contentDelay = 0.1;
        gsap.killTweensOf(expand);

        Flip.fit(expand, origin, { duration: 0, props: expandProp });
        gsap.set(expand, { autoAlpha: 1 });
        gsap.set(panel, { backgroundColor: "transparent" });

        const grow = Flip.fit(expand, panel, {
            duration: 0.6,
            ease: "expo.out",
            absolute: true,
            props: expandProp,
        }) as gsap.core.Tween;

        gsap.timeline({
            onComplete: () => {
                isAnimating = false;
            },
        })
            .add(grow)
            .to(menu, { autoAlpha: 1, duration: 0.15 }, contentDelay)
            .set(panel, { clearProps: "backgroundColor" })
            .set(expand, { autoAlpha: 0 });

        if (panelContent) {
            gsap.killTweensOf(panelContent);
            gsap.set(panelContent, { autoAlpha: 1 });
            revealText(panelContent, contentDelay);
            showActiveChip(panelContent, contentDelay + 0.3);
        }
    } else {
        if (panelContent) {
            gsap.set(panelContent, { autoAlpha: 1 });
            const activeLink = panelContent.querySelector<HTMLElement>(".menu__dsk__nav__list a.active");
            if (activeLink) gsap.set(activeLink, { "--menu-chip-opacity": 1, "--menu-chip-scale": 1 });
        }

        gsap.to(menu, {
            autoAlpha: 1,
            duration: 0.2,
            onComplete: () => {
                isAnimating = false;
            },
        });
    }
}

function showActiveChip(panelContent: HTMLElement, delay: number) {
    const activeLink = panelContent.querySelector<HTMLElement>(".menu__dsk__nav__list a.active");
    if (!activeLink) return;

    gsap.killTweensOf(activeLink);
    gsap.fromTo(activeLink, { "--menu-chip-opacity": 0, "--menu-chip-scale": 0 }, { "--menu-chip-opacity": 1, "--menu-chip-scale": 1, duration: 0.4, ease: "expo.out", delay });
}

function hideActiveChip(panelContent: HTMLElement): gsap.core.Tween | gsap.core.Timeline {
    const activeLink = panelContent.querySelector<HTMLElement>(".menu__dsk__nav__list a.active");
    if (!activeLink) return gsap.timeline();

    gsap.killTweensOf(activeLink);

    return gsap.to(activeLink, {
        "--menu-chip-opacity": 0,
        "--menu-chip-scale": 0,
        duration: 0.25,
        ease: "expo.out",
    });
}

export default function initMenu() {
    const menu = document.querySelector<HTMLDialogElement>(".menu");
    if (!menu) return;

    menuEl = menu;

    menuTrigger = document.querySelector<HTMLButtonElement>(".menu__trigger");
    if (menuTrigger) {
        menuTrigger.hidden = false;
        createMenuTriggerScrollTrigger();
    }

    document.body.addEventListener("click", event => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const trigger = target.closest<HTMLElement>('button[aria-controls="menu"]');
        if (!trigger) return;

        event.preventDefault();
        openMenu(trigger);
    });

    menu.addEventListener("click", event => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        if (!target.closest(".menu__inner")) {
            void closeMenu();
            return;
        }

        if (target.closest("[data-close-menu]")) {
            void closeMenu();
            return;
        }

        if (target.closest("a[href]")) {
            void closeMenu({ keepTriggerHidden: true });
            return;
        }
    });

    document.addEventListener("keydown", event => {
        if (event.key !== "Escape") return;
        if (!menu.open) return;

        void closeMenu();
    });

    menu.addEventListener("close", () => {
        isAnimating = false;
        recomputeCursorState();

        setTriggerExpanded(false);
        if (menuTrigger && !suppressTriggerShow) {
            gsap.to(menuTrigger, { autoAlpha: isTriggerActive() ? 1 : 0, duration: 0.2 });
        }

        suppressTriggerShow = false;

        startScroller();
    });
}
